import React, { Fragment } from 'react';
import { Divider, Grid } from '@mui/material';
import { ReportComponentConfig, ReportFormat, ReportToSort } from '../store/types';
import { DateRange } from 'src/store';
import { DateTime } from 'luxon';
import { ClientAccountSubType } from 'src/common/types';
import { Props } from '../container';

interface ReportingComponentListProps extends Props {
  reportingList: ReportComponentConfig[];
  allowConsolidation?: boolean;
  fullReports: boolean;
}

export const ReportingComponentList = (props: ReportingComponentListProps) => {
  const createReportSortRequest = (isAdd: boolean, id: number, text: string) => {
    const input: ReportToSort = { id: id, text: text };
    let result: ReportToSort[] = [];

    if (isAdd) {
      result = insertItem(props.parameters.reportsToSort, { item: input, index: props.parameters.reportsToSort.length });
      props.setSelectedReportOrderParameter(result);
    } else {
      const index = props.parameters.reportsToSort.findIndex((x) => x.id === input.id);
      if (index > -1) {
        result = removeItem(props.parameters.reportsToSort, { item: input, index: index });
        props.setSelectedReportOrderParameter(result);
      }
    }
  };

  const isDatesLessThanFullYear = (dates: DateRange): boolean => {
    const dateFrom = DateTime.fromISO(dates.dateFrom).startOf('day');
    const dateTo = DateTime.fromISO(dates.dateTo).startOf('day');
    const diffInDays = dateTo.diff(dateFrom, 'days').plus({ days: 1 }).days;
    return diffInDays < 365;
  };

  const insertItem = (array: ReportToSort[], action: { item: ReportToSort; index: number }) => {
    const newArray = array.slice();
    newArray.splice(action.index, 0, action.item);
    return newArray;
  };

  const removeItem = (array: ReportToSort[], action: { item: ReportToSort; index: number }) => {
    const newArray = array.slice();
    newArray.splice(action.index, 1);
    return newArray;
  };

  const isRenderable = (value: ReportComponentConfig): boolean => {
    if (value.supportedFormats && !value.supportedFormats.includes(props.parameters.reportFormat as ReportFormat)) {
      return false;
    }
    if (value.isAdminOnly && !props.hasClientEditAdminOnlyPermission) {
      return false;
    }
    if (value.featureFlagged && !props.fullReports) {
      return false;
    }
    if (value.excelFeatureFlagged && props.parameters.reportFormat === ReportFormat.Excel && !props.fullReports) {
      return false;
    }
    const isSuperOrPension: boolean =
      props.parameters.clientSubTypeId === ClientAccountSubType.Pension.id || props.parameters.clientSubTypeId === ClientAccountSubType.Super.id;
    if (value.nonSuperOnly && !isSuperOrPension) {
      return false;
    }
    return true;
  };

  return (
    <>
      {props.reportingList.map((value: ReportComponentConfig, index: number) => {
        const ReportComponent = value.component;
        if (!isRenderable(value)) {
          return null;
        }
        return (
          <Fragment key={index}>
            <Grid item>
              <ReportComponent
                createReportSortRequest={createReportSortRequest}
                isDatesLessThanFullYear={isDatesLessThanFullYear}
                {...props}
                setGroupConsolidatedOption={props.allowConsolidation ? props.setGroupConsolidatedOption : undefined}
              />
            </Grid>
            {index < props.reportingList.length - 1 && (
              <Grid item>
                <Divider />
              </Grid>
            )}
          </Fragment>
        );
      })}
    </>
  );
};
